'use client'

import {
  ClipboardEvent,
  FC,
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Button, Input } from '@/components/commons'
import { ArrowLeftIcon, EyeIcon, EyeNoIcon } from '@/components/icons'
import { yupResolver } from '@hookform/resolvers/yup'

import { StepEnum } from '../ForgotPassword/types'
import styles from './styles.module.css'
import { FormProps, IResetPasswordFormProps } from './types'
import { schemaResetPassword } from './utils/validations'

const ResetPasswordForm: FC<IResetPasswordFormProps> = ({
  className,
  handleResetPassword,
  sendButtonLoading,
  contacts,
  selectedTenantId,
  setStep,
  callToAction,
}) => {
  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    reset,
    trigger,
    formState: { isValid, errors },
  } = useForm<FormProps>({
    mode: 'onChange',
    resolver: yupResolver(schemaResetPassword),
    defaultValues: {
      first: '',
      second: '',
      third: '',
      fourth: '',
      fifth: '',
      sixth: '',
      password: '',
      confirmPassword: '',
    },
  })

  const [toggleEyePassword, setToggleEyePassword] = useState(false)
  const [toggleEyeConfirmPassword, setToggleEyeConfirmPassword] =
    useState(false)

  // Resetar formulário quando o componente monta
  useEffect(() => {
    reset({
      first: '',
      second: '',
      third: '',
      fourth: '',
      fifth: '',
      sixth: '',
      password: '',
      confirmPassword: '',
    })
    setToggleEyePassword(false)
    setToggleEyeConfirmPassword(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const codeInputList = useMemo(() => {
    return ['first', 'second', 'third', 'fourth', 'fifth', 'sixth']
  }, [])

  function handleInputChange(
    event: FormEvent<HTMLInputElement>,
    next?: keyof FormProps,
    prev?: keyof FormProps,
  ) {
    const value = event.currentTarget.value

    if (value?.length === 1) {
      setFocus(next || 'password')
    } else if (value?.length === 0 && prev) {
      setFocus(prev)
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault()
    const pasteData = event.clipboardData.getData('text')
    const chars = pasteData.slice(0, 6).split('')

    codeInputList.forEach((field, index, array) => {
      setValue(field as keyof FormProps, chars[index] || '')
      if (chars[index]) {
        setFocus((array[index + 1] as keyof FormProps) || 'password')
      }
    })
  }

  const onSubmit: SubmitHandler<FormProps> = async (data) => {
    const code = `${data.first}${data.second}${data.third}${data.fourth}${data.fifth}${data.sixth}`
    await handleResetPassword({
      code,
      newpassword: data.confirmPassword,
      document: contacts.listForResetByTenant[0]?.document || '',
      tenantId: selectedTenantId,
    })
  }

  return (
    <form
      className={`flex flex-col w-full bg-white gap-4 ${className}`}
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <div className="flex flex-col w-full gap-2">
        <p className="prose-body text-primary-900 w-full">
          Insira o código recebido
        </p>
        <div className="flex w-full gap-2 justify-between">
          {codeInputList.map((field, index, array) => (
            <Input
              key={field}
              {...register(field as keyof FormProps)}
              className={styles.wrapperInput}
              maxLength={1}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              data-form-type="other"
              onChange={(e) =>
                handleInputChange(
                  e,
                  array[index + 1] as keyof FormProps,
                  array[index - 1] as keyof FormProps,
                )
              }
              onPaste={handlePaste}
            />
          ))}
        </div>
      </div>

      {/* Inputs hidden para confundir o autopreenchimento do navegador */}
      <input
        type="text"
        autoComplete="off"
        style={{ display: 'none' }}
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        type="password"
        autoComplete="new-password"
        style={{ display: 'none' }}
        tabIndex={-1}
        aria-hidden="true"
      />

      <Input
        wFull
        label="Nova senha"
        placeholder="*****"
        helperText={errors.password?.message || ''}
        type={toggleEyePassword ? 'text' : 'password'}
        autoCapitalize="none"
        autoComplete="new-password"
        autoCorrect="off"
        spellCheck="false"
        data-form-type="other"
        classNameIconRight="cursor-pointer"
        iconRight={
          toggleEyePassword ? (
            <EyeIcon onClick={() => setToggleEyePassword(!toggleEyePassword)} />
          ) : (
            <EyeNoIcon
              onClick={() => setToggleEyePassword(!toggleEyePassword)}
            />
          )
        }
        {...register('password', {
          onChange: () => {
            trigger('confirmPassword')
          },
        })}
      />

      <Input
        wFull
        label="Confirmar nova senha"
        placeholder="*****"
        helperText={errors.confirmPassword?.message || ''}
        type={toggleEyeConfirmPassword ? 'text' : 'password'}
        autoCapitalize="none"
        autoComplete="new-password"
        autoCorrect="off"
        spellCheck="false"
        data-form-type="other"
        classNameIconRight="cursor-pointer"
        iconRight={
          toggleEyeConfirmPassword ? (
            <EyeIcon
              onClick={() =>
                setToggleEyeConfirmPassword(!toggleEyeConfirmPassword)
              }
            />
          ) : (
            <EyeNoIcon
              onClick={() =>
                setToggleEyeConfirmPassword(!toggleEyeConfirmPassword)
              }
            />
          )
        }
        {...register('confirmPassword')}
      />

      <div className="flex w-full gap-4">
        <Button
          type="button"
          text="Voltar"
          variant="secondary"
          className="flex-1"
          iconLeft={<ArrowLeftIcon />}
          onClick={() =>
            callToAction ? callToAction() : setStep(StepEnum.CONTACT)
          }
          disabled={sendButtonLoading}
        />
        <Button
          type="submit"
          text="Alterar senha"
          disabled={!isValid || sendButtonLoading}
          className="flex-1"
          isLoading={sendButtonLoading}
        />
      </div>
    </form>
  )
}

export default ResetPasswordForm
